// Shared donut-with-legend scaffold — the recharts PieChart + absolute
// center-total overlay + color-dot legend list shape that both
// CallOutcomesDonut.tsx (Leads & Revenue lane) and DemographicDonut.tsx
// (Demographics lane) independently built byte-for-byte identically
// (ResponsiveContainer sizing, innerRadius 48/outerRadius 72, white 2px
// slice stroke, `pointer-events-none` center overlay, `min-w-0` legend
// list). Extracted here (2026-08-10, PHASE-C3) so a third donut doesn't
// triplicate it.
//
// CallOutcomesDonut.tsx is NOT touched or migrated by this extraction —
// it belongs to the Leads & Revenue lane, and its own legend row has an
// intentional deviation from this scaffold (no `truncate` class, by
// design — see that file's comment on why: `truncate` disables flexbox's
// min-content protection and squeezes labels down to 2-3 characters
// before an ellipsis on a narrow card). Adopting this scaffold is their
// call, not something this extraction forces on them.
//
// Callers own their own color/value-derivation logic (categorical vs.
// ordinal palettes, unit-word pluralization, etc.) and pass in fully
// resolved slices plus small formatter callbacks for the two things that
// differ between existing callers: the tooltip string and the legend's
// trailing value text.

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { ReactNode } from "react";

export interface DonutLegendSlice {
  key: string; // stable React key -- distinct from `label` since two slices could share a display label
  label: string;
  value: number;
  color: string;
}

export function DonutWithLegend({
  slices,
  height = 180,
  centerLabel,
  emptyMessage,
  tooltipFormatter,
  legendValueFormatter,
}: {
  /** All slices, including zero-value ones -- the legend lists every one; only nonzero slices render as pie wedges. */
  slices: DonutLegendSlice[];
  height?: number;
  /** Text under the center total, e.g. "Clicks" or "calls". */
  centerLabel: string;
  emptyMessage: string;
  /** `name` is recharts' own resolved series name (from the Pie's `nameKey`) -- pass it through, don't recompute it. Return shape matches recharts Tooltip's `formatter`: [value, name]. */
  tooltipFormatter: (value: number, name: string, total: number) => [string, string];
  legendValueFormatter: (slice: DonutLegendSlice, total: number) => ReactNode;
}) {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const visible = slices.filter((s) => s.value > 0);

  if (total <= 0) {
    return (
      <div
        className="flex items-center justify-center text-dash-context text-muted-foreground"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4" style={{ height }}>
      <div className="relative h-full w-[150px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={visible}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={48}
              outerRadius={72}
              paddingAngle={2}
              stroke="#FFFFFF"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {visible.map((s) => (
                <Cell key={s.key} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: "1px solid var(--color-border)",
                fontSize: 12,
              }}
              formatter={(value: number, name) => tooltipFormatter(value, String(name), total)}
            />
          </PieChart>
        </ResponsiveContainer>
        {/* Center total — pointer-events-none so the pie's hover/tooltip
            layer underneath stays interactive. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="tabular text-dash-value font-semibold text-foreground">
            {total.toLocaleString()}
          </span>
          <span className="text-dash-context text-muted-foreground">{centerLabel}</span>
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5">
        {slices.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
            <span className="truncate text-dash-context text-foreground" title={s.label}>
              {s.label}
            </span>
            <span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
              {legendValueFormatter(s, total)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
